// ** React Imports
import { useState } from "react";

// ** MUI Components
import {
  Avatar,
  Stepper,
  StepLabel,
  Button,
  Box,
  Typography,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import MuiStep from "@mui/material/Step";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Custom Component Imports
import StepperCustomDot from "../../../components/Stepper/StepperCustomDot";
import CustomAvatar from "../../../components/mui/avatar";

// ** Styled Component
import StepperWrapper from "../../../components/styles/mui/stepper";

// ** Utils Import
import { hexToRGBA } from "../../../components/utils/hex-to-rgba";

// ** Third Party Imports
import Draggable from "react-draggable";

import { useCreateEstimateLogic } from "../logic/create-estimate-logic";

// ** Component Imports
import AddCustomerDialog from "screens/Customers/component/AddCustomer";
import AddContactDialog from "screens/Customers/component/AddContact";
import TagManagementDialog from "screens/Customers/component/TagManagement";
import AddBundleDialog from "screens/Assessments/component/AddBundle";
import AddServiceDialog from "screens/Assessments/component/AddService";
import PreAttachment from "screens/Requests/component/PreAttachment";
import AddJobsiteDialog from "screens/Customers/component/AddJobsite";

const steps = [
  {
    serial: "1",
    title: "Estimate Details",
    icon: "tabler:list-details",
  },
  {
    serial: "2",
    title: "Work Requirements",
    icon: "tabler:align-box-left-stretch",
  },
  {
    serial: "3",
    title: "Link Records",
    icon: "tabler:link",
  },
  {
    serial: "4",
    title: "Notes",
    icon: "tabler:ballpen",
  },
  {
    serial: "5",
    title: "Assign To",
    icon: "tabler:user-share",
  },
];

const Step = styled(MuiStep)(({ theme }) => ({
  "& .MuiStepLabel-root": {
    paddingTop: 0,
  },
  "& .MuiStepLabel-iconContainer": {
    display: "none",
  },
  "& .step-subtitle": {
    color: `${theme.palette.text.disabled} !important`,
  },
  "& + svg": {
    color: theme.palette.text.disabled,
  },
  "&.Mui-completed .step-title": {
    color: theme.palette.text.disabled,
  },
}));

const StepperHeaderContainer = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    borderRight: 0,
    borderBottom: "none",
  },
}));

function DraggablePaper(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const CreateEstimate = ({ open, handleClose, setLoading }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { CreateEstimateProps } = useCreateEstimateLogic({
    handleClose,
    activeStep,
    setActiveStep,
    steps,
    setLoading,
  });

  const {
    isServiceEmpty,
    setIsServiceEmpty,
    handleOpenServiceDlg,
    handleCloseServiceDlg,
    openServiceDlg,
    openAddBundle,
    setOpenAddBundle,
    isBundleEmpty,
    setIsBundleEmpty,
    handleOpenBundleDlg,
    handleCloseBundleDlg,
    handleSave,
    renderContent,
    handleBack,
    handleEstimateNext,
    allCustomers,
    allContacts,
    openNewCustomerDlg,
    setOpenNewCustomerDlg,
    fetchData,
    openNewContactDlg,
    handleCloseContactDialog,
    customer,
    fetchContacts,
    toast,    
    openNewJobsiteDlg,
    setOpenNewJobsiteDlg,
    propertyArea,
    areaMeasurement,
    activeStepJobsite,
    jobsiteContent,
    clearPropertyArea,
    fetchPropertyArea,
    clearAreaMeasurement,
    setActiveStepJobsite,
    setPropertyArea,
    setAreaMeasurement,
    navigate,
    setAlertList,
    handleJobsiteChange,
    openTagManagementDlg,
    handleCloseNewJobsiteDlg,
    alertList,
    files,
    setFiles,
    jobsite,
    NProgress,
    fetchJobsites,
    propertyAreaList,
    handleCloseTagManagementDlg,
    handleAddTag,
    handleDeleteTag,
    serviceRequest,
    Tag,
    setTag,
    resetRequest,
    AddServiceProps,
    AddBundleProps,
    WorkRequirementsProps,
    AttachmentProps,
    ResetWorkRequirement,
    handleSaveAsDraft
  } = CreateEstimateProps;

  const handleEstimateClose = () => {
    handleClose(false);
    setActiveStep(0);
    resetRequest();
    ResetWorkRequirement();
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={open}
        onClose={() => handleEstimateClose()}
        PaperComponent={isSmallScreen ? Paper : DraggablePaper}
        aria-labelledby="form-dialog-title"
         PaperProps={{
          sx: {
            height: "100%",
            maxHeight: "95vh",
            width: isSmallScreen ? "95vw" : "auto",
            m: isSmallScreen ? 2 : 0,
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: isSmallScreen ? "flex-end" : "center",
          },
        }}
      >
        <DialogTitle
          style={{ cursor: "move", borderBottom: "1px solid #D9D9D9" }}
          id="draggable-dialog-title"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: isSmallScreen ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Typography variant="h3" fontWeight="bold">
                Create Estimate
              </Typography>
            </Box>
            <StepperHeaderContainer
              sx={{
                width: isSmallScreen ? "100%" : "auto",
                pt: isSmallScreen ? 0 : 0,
              }}
            >
              <StepperWrapper>
                <Stepper
                  activeStep={activeStep}
                  connector={<></>}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: isSmallScreen ? "wrap" : "nowrap",
                    paddingTop: isSmallScreen ? "0" : "1.5rem !important",
                    gap: isSmallScreen ? "5px" : "16px",
                    "& .MuiStep-root": {
                      padding: isSmallScreen ? "5px" : "0",
                      flex: isSmallScreen ? "1 1 auto" : "none",
                    },
                  }}
                >
                  {steps.map((step, index) => {
                    const RenderAvatar =
                      activeStep >= index ? CustomAvatar : Avatar;
                    const isStepActive = activeStep === index;
                    return (
                      <Step key={index} sx={{ paddingBottom: 0 }}>
                        <StepLabel StepIconComponent={StepperCustomDot}>
                          <div className="step-label">
                            <RenderAvatar
                              variant="rounded"
                              {...(activeStep >= index && {
                                skin: "light",
                              })}
                              {...(isStepActive && { skin: "filled" })}
                              {...(activeStep >= index && {
                                color: "success",
                              })}
                              sx={{
                                ...(isStepActive && {
                                  boxShadow: (theme) => theme.shadows[3],
                                  color: "#fff", // white when active
                                }),
                                ...(activeStep > index && {
                                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.48)' : hexToRGBA(theme.palette.primary.main, 0.4),
                                }),
                              }}
                            >
                              <Icon icon={step.icon} />
                            </RenderAvatar>
                            <div>
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                {...(activeStep === index && {
                                  fontWeight: "550",
                                  color: "text.primary",
                                })}
                              >
                                {step.title}
                              </Typography>
                            </div>
                          </div>
                        </StepLabel>
                      </Step>
                    );
                  })}
                </Stepper>
              </StepperWrapper>
            </StepperHeaderContainer>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ position: "relative" }}>
          <Box sx={{ height: "100%" }}>{renderContent()}</Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: isSmallScreen ? "16px !important" : "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
            flexDirection: isSmallScreen ? "column-reverse" : "row",
            gap: isSmallScreen ? 2 : 0,
          }}
          className="dialog-actions-dense"
        >
          <Box sx={{ width: isSmallScreen ? "100%" : "auto" }}>
            <Button
              variant="contained"
              color="info"
              onClick={() => handleEstimateClose()}
              fullWidth={isSmallScreen}
            >
              Cancel
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              width: isSmallScreen ? "100%" : "auto",
              gap: isSmallScreen ? 2 : 2,
              flexDirection: isSmallScreen ? "column" : "row",
            }}
          >
            {activeStep !== 0 && (
              <>
                {/* <Button variant="tonal" color="info">
                  Skip
                </Button> */}
                <Button
                variant="tonal" 
                color="info"
                onClick={handleSaveAsDraft}>
                Save as Draft
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              color="main"
              disabled={activeStep === 0}
              onClick={handleBack}
              fullWidth={isSmallScreen}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="main"
              onClick={handleEstimateNext}
              fullWidth={isSmallScreen}
            >
              {activeStep === steps.length - 1 ? "Save Estimate" : "Next Step"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <AddCustomerDialog
        open={openNewCustomerDlg}
        setOpenNewCustomerDlg={setOpenNewCustomerDlg}
        fetchData={fetchData}
        allCustomers={allCustomers}
      />
     
      <AddJobsiteDialog
             open={openNewJobsiteDlg}
             handleClose={handleCloseNewJobsiteDlg}
             setJobsiteOpen={setOpenNewJobsiteDlg}
             customer={customer}
             fetchContactData={fetchContacts}
             toast={toast}
             jobsite={jobsite}
             handleJobsiteChange={handleJobsiteChange}
             navigate={navigate}
             NProgress={NProgress}
             fetchContacts={fetchContacts}
             fetchJobsites={fetchJobsites}
             contacts={allContacts}
             propertyArea={propertyArea}
             setPropertyArea={setPropertyArea}
             areaMeasurement={areaMeasurement}
             setAreaMeasurement={setAreaMeasurement}
             setLoading={setLoading}
             activeStep={activeStepJobsite}
             setActiveStep={setActiveStepJobsite}
             clearAreaMeasurement={clearAreaMeasurement}
             fetchPropertyArea={fetchPropertyArea}
             propertyAreaList={propertyAreaList}
             clearPropertyArea={clearPropertyArea}
             jobsiteContent={jobsiteContent}
             alertList={alertList}
             setAlertList={setAlertList}
             files={files}
             setFiles={setFiles}
           />
     
      <AddContactDialog
        open={openNewContactDlg}
        handleClose={handleCloseContactDialog}
        customer={customer}
        setLoading={setLoading}
        fetchContactData={fetchContacts}
        toast={toast}
        contacts={allContacts}
      />

      <AddServiceDialog {...AddServiceProps} />

      <AddBundleDialog {...AddBundleProps} />

      <PreAttachment Title="Upload Attachment" {...AttachmentProps} />

      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: "Tag Management" }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={serviceRequest?.tags}
        tag={Tag}
        setTag={setTag}
      />
    </>
  );
};

export default CreateEstimate;
